// ConditionReportsPage — /condition-reports staff TRACKING surface (design §7).
//
// Two sections:
//   (a) Tracking — active/recent move-in condition reports with a live status
//       badge, unit/title, residents + Gary per-signer progress (X-of-N), and
//       key timestamps.
//   (b) Exceptions — records the auto-flow parked in `exception`, grouped by
//       exceptionCode, each with the retry (re-arm a send-path failure) and
//       resolve (acknowledge/dismiss) actions.
//
// READ/OPS ONLY — listing never triggers a send; retry only re-arms a render/
// DocuSeal failure back to `ready` for the watcher, and resolve just dismisses.
// Nothing here changes the dormant send posture.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  listConditionReports,
  retryConditionReport,
  resolveConditionReport,
  type ConditionReportsSummary,
  type ConditionReportTrackingRow,
  type ConditionReportSignerProgress,
  type ConditionReportExceptionGroup,
  type ConditionReportExceptionRow,
  type ConditionReportStatus,
  type ConditionReportExceptionCode,
  type SignerStatus,
} from '@/lib/conditionReportsClient';
import { AppfolioSyncChip } from '@/components/AppfolioSyncChip';
import { CurrentDocumentDownload } from '@/components/CurrentDocumentDownload';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral';

// --- lifecycle status → badge -------------------------------------------------

const STATUS_META: Record<ConditionReportStatus, { label: string; tone: PillTone }> = {
  polling: { label: 'Polling', tone: 'neutral' },
  ready: { label: 'Ready to send', tone: 'info' },
  sending: { label: 'Sending', tone: 'info' },
  sent: { label: 'Out for signature', tone: 'info' },
  gary_pending: { label: 'Awaiting countersign', tone: 'accent' },
  completed: { label: 'Complete', tone: 'success' },
  exception: { label: 'Exception', tone: 'danger' },
};

function statusMeta(status: ConditionReportStatus): { label: string; tone: PillTone } {
  return STATUS_META[status] ?? { label: status, tone: 'neutral' };
}

// --- per-signer status → badge ------------------------------------------------

const SIGNER_META: Record<SignerStatus, { label: string; tone: PillTone }> = {
  pending: { label: 'Pending', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'info' },
  opened: { label: 'Opened', tone: 'info' },
  completed: { label: 'Signed', tone: 'success' },
  declined: { label: 'Declined', tone: 'danger' },
};

// --- exception code → human label --------------------------------------------

const EXCEPTION_META: Record<ConditionReportExceptionCode, { label: string; hint: string }> = {
  ambiguous_unit: {
    label: 'Ambiguous unit',
    hint: 'The zInspector AccountingID resolved to more than one AppFolio unit.',
  },
  not_linked: {
    label: 'Not linked to AppFolio',
    hint: 'The zInspector property has no AccountingID and its address didn’t match exactly one AppFolio unit. Set the AccountingID on the zInspector property so it can be auto-sent.',
  },
  no_tenants: {
    label: 'No tenants to send to',
    hint: 'The resolved unit has no current or future tenants in AppFolio to send to.',
  },
  tenant_no_email: {
    label: 'Resident missing email',
    hint: 'A current resident signer has no email on file.',
  },
  stuck_inspection: {
    label: 'Stuck inspection',
    hint: 'The inspection was still unfinished in zInspector after 90 days. Finish or close it there, then ask for the report to be re-queued.',
  },
  render_failed: {
    label: 'Render failed',
    hint: 'The zInspector report could not be rendered to a signable PDF.',
  },
  docuseal_failed: {
    label: 'DocuSeal failed',
    hint: 'The PDF rendered but DocuSeal refused the submission.',
  },
};

function exceptionMeta(code: ConditionReportExceptionCode): { label: string; hint: string } {
  return EXCEPTION_META[code] ?? { label: code, hint: '' };
}

function fmtTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function unitLabel(row: { propertyLabel?: string; accountingId?: string }): string {
  // Prefer the HUMAN property line (street address, else inspection title) — never
  // a raw AppFolio unit UUID. Fall back to the AccountingID only as provenance.
  if (row.propertyLabel?.trim()) return row.propertyLabel.trim();
  if (row.accountingId) return `Acct ${row.accountingId}`;
  return '—';
}

// =============================================================================
// Page
// =============================================================================

export function ConditionReportsPage() {
  const [summary, setSummary] = useState<ConditionReportsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // WEL-25 quicklook: the tracking row whose expanded detail is open.
  const [openDocId, setOpenDocId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await listConditionReports());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load condition reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const tracking = summary?.tracking ?? [];
  const exceptions = summary?.exceptions ?? [];
  const exceptionCount = summary?.exceptionCount ?? 0;
  const openRow = tracking.find((r) => r.zInspectorDocumentId === openDocId) ?? null;

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Condition reports
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Move-in inspection reports out for signature: residents in parallel, then Gary countersigns last.
          Auto-flow failures surface below for you to retry or resolve.
        </p>
      </div>

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      )}

      {!loading && !error && summary && (
        <div className="stack" style={{ gap: 'var(--space-8)' }}>
          {/* (b) Exceptions FIRST when present — they need attention. */}
          {exceptionCount > 0 && (
            <ExceptionsSection groups={exceptions} onChanged={() => void load()} />
          )}

          {/* (a) Tracking */}
          <section className="stack" style={{ gap: 'var(--space-4)' }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-bold)', margin: 0 }}>
              Tracking
            </h2>
            {tracking.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
                No active condition reports. They appear here once the watcher discovers a move-in inspection.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table" data-zebra="true">
                  <thead>
                    <tr>
                      <th scope="col">Property</th>
                      <th scope="col">Residents</th>
                      <th scope="col">Status</th>
                      <th scope="col">Signers</th>
                      <th scope="col">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracking.map((row) => (
                      <TrackingRow
                        key={row.zInspectorDocumentId}
                        row={row}
                        onOpen={() => setOpenDocId(row.zInspectorDocumentId)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      <TrackingDetailModal
        row={openRow}
        open={openRow !== null}
        onClose={() => setOpenDocId(null)}
      />
    </div>
  );
}

// =============================================================================
// (a) Tracking row
// =============================================================================

function TrackingRow({
  row,
  onOpen,
}: {
  row: ConditionReportTrackingRow;
  onOpen: () => void;
}): React.JSX.Element {
  const meta = statusMeta(row.status);
  return (
    <tr
      onClick={onOpen}
      style={{ cursor: 'pointer' }}
      tabIndex={0}
      role="button"
      aria-label={`Open details for ${row.propertyLabel}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 'var(--weight-medium)' }}>{row.propertyLabel || '(unresolved unit)'}</div>
        {row.title && row.title !== row.propertyLabel && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)' }}>{row.title}</div>
        )}
        {row.appfolioSync && (
          <div><AppfolioSyncChip sync={row.appfolioSync} /></div>
        )}
      </td>
      <td style={{ fontSize: 'var(--text-sm)' }}>{row.residentNames || '—'}</td>
      <td>
        <span className="pill" data-tone={meta.tone}>{meta.label}</span>
      </td>
      <td>
        <SignersCell row={row} />
      </td>
      <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>
        {fmtTime(row.updatedAt)}
      </td>
    </tr>
  );
}

function SignersCell({ row }: { row: ConditionReportTrackingRow }): React.JSX.Element {
  if (row.signers.length === 0) {
    return <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-sm)' }}>{row.signingLabel}</span>;
  }
  const residents = row.signers.filter((s) => !s.isCountersigner);
  const gary = row.signers.find((s) => s.isCountersigner) ?? null;
  // WEL-52: the "#/# signed #/# residents" summary header was redundant with the
  // per-signer detail below it (Andrea), so it's removed — the Status badge plus
  // the per-signer rows already convey the signing state.
  return (
    <div className="stack-sm" style={{ gap: 'var(--space-2)' }}>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {residents.map((s) => (
          <SignerLine key={`${s.role}-${s.email}`} signer={s} />
        ))}
        {gary && <SignerLine signer={gary} />}
      </ul>
    </div>
  );
}

function SignerLine({ signer }: { signer: ConditionReportSignerProgress }): React.JSX.Element {
  const meta = SIGNER_META[signer.status] ?? { label: signer.status, tone: 'neutral' as PillTone };
  const who = signer.name || signer.email;
  const label = signer.isCountersigner ? `${who} (Gary, countersign)` : who;
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
      <span className="pill" data-tone={meta.tone} data-size="sm">{meta.label}</span>
      <span style={{ color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </li>
  );
}

// =============================================================================
// (a) Tracking quicklook — expanded per-report detail (WEL-25)
// =============================================================================

function TrackingDetailModal({
  row,
  open,
  onClose,
}: {
  row: ConditionReportTrackingRow | null;
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const containerRef = useFocusTrap(open);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEscapeKey(onClose, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open || !row) return null;
  const meta = statusMeta(row.status);
  const residents = row.signers.filter((s) => !s.isCountersigner);
  const gary = row.signers.find((s) => s.isCountersigner) ?? null;

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cr-modal-title"
        className="modal"
        data-size="lg"
      >
        <div className="modal-header">
          <div className="stack-sm" style={{ minWidth: 0 }}>
            <h2 id="cr-modal-title" className="modal-title">{row.propertyLabel || '(unresolved unit)'}</h2>
            {row.title && row.title !== row.propertyLabel && (
              <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{row.title}</div>
            )}
          </div>
          <span className="pill" data-tone={meta.tone} style={{ alignSelf: 'flex-start' }}>{meta.label}</span>
          <button
            ref={closeRef}
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            data-icon-only
            aria-label="Close"
            onClick={onClose}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div className="stack">
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <CrKeyVal label="Residents" value={row.residentNames || '—'} />
              <CrKeyVal
                label="Signing"
                value={`${row.signingLabel} · ${row.residentsSigned}/${row.residentsTotal} residents${row.countersignerSigned ? ' · WHP signed' : ''}`}
              />
              <CrKeyVal label="First seen" value={fmtTime(row.firstSeenAt)} />
              <CrKeyVal label="Last updated" value={fmtTime(row.updatedAt)} />
              {row.lastPolledAt && <CrKeyVal label="Last polled" value={fmtTime(row.lastPolledAt)} />}
              {row.completionNotifiedAt && (
                <CrKeyVal label="Completed" value={fmtTime(row.completionNotifiedAt)} />
              )}
              <CrKeyVal label="Unit" value={unitLabel(row)} />
              {row.appfolioSync && (
                <CrKeyVal
                  label="AppFolio"
                  value={`${row.appfolioSync.label}${row.appfolioSync.attachmentId ? ` · attachment ${row.appfolioSync.attachmentId}` : ''}${row.appfolioSync.error ? ` · ${row.appfolioSync.error}` : ''}`}
                />
              )}
            </div>

            <div className="stack-sm">
              <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' }}>
                Signers
              </div>
              {row.signers.length === 0 ? (
                <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-sm)' }}>
                  {row.signingLabel} — no signer links sent yet.
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">Signer</th>
                        <th scope="col">Status</th>
                        <th scope="col">Opened</th>
                        <th scope="col">Signed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...residents, ...(gary ? [gary] : [])].map((s) => {
                        const sm = SIGNER_META[s.status] ?? { label: s.status, tone: 'neutral' as PillTone };
                        return (
                          <tr key={`${s.role}-${s.email}`}>
                            <td>
                              <div>{s.name || s.email}</div>
                              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)' }}>
                                {s.isCountersigner ? 'WHP countersigner' : s.email}
                              </div>
                            </td>
                            <td><span className="pill" data-tone={sm.tone} data-size="sm">{sm.label}</span></td>
                            <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>
                              {fmtTime(s.openedAt)}
                            </td>
                            <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>
                              {fmtTime(s.completedAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          {/* Current-state report download: executed → signed PDF; mid-signing →
              the live DocuSeal combined PDF; pre-send reports have no PDF yet.
              The CR signature record's groupId IS the zInspector document id. */}
          <CurrentDocumentDownload
            groupId={row.zInspectorDocumentId}
            documentType="condition_report"
            label="Download report"
            size="md"
            unavailableNote="No report PDF yet — it exists once signing starts."
          />
          {row.docusealUrl && (
            <a
              className="btn"
              data-variant="secondary"
              href={row.docusealUrl}
              target="_blank"
              rel="noreferrer"
            >
              View in DocuSeal ↗
            </a>
          )}
          <button type="button" className="btn" data-variant="secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

function CrKeyVal({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'baseline' }}>
      <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', minWidth: '7rem' }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)' }}>{value}</span>
    </div>
  );
}

// =============================================================================
// (b) Exceptions
// =============================================================================

function ExceptionsSection({
  groups,
  onChanged,
}: {
  groups: ConditionReportExceptionGroup[];
  onChanged: () => void;
}): React.JSX.Element {
  const total = groups.reduce((n, g) => n + g.count, 0);
  return (
    <section className="stack" style={{ gap: 'var(--space-4)' }}>
      <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'center' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-bold)', margin: 0 }}>
          Exceptions
        </h2>
        <span className="pill" data-tone="danger">{total}</span>
      </div>
      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        {groups.map((g) => (
          <ExceptionGroupCard key={g.exceptionCode} group={g} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}

function ExceptionGroupCard({
  group,
  onChanged,
}: {
  group: ConditionReportExceptionGroup;
  onChanged: () => void;
}): React.JSX.Element {
  const meta = exceptionMeta(group.exceptionCode);
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div>
        <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'center' }}>
          <span style={{ fontWeight: 'var(--weight-bold)' }}>{meta.label}</span>
          <span className="pill" data-tone="warning">{group.count}</span>
        </div>
        {meta.hint && (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-1)' }}>
            {meta.hint}
          </div>
        )}
      </div>
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        {group.rows.map((row) => (
          <ExceptionRowItem key={row.zInspectorDocumentId} row={row} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function ExceptionRowItem({
  row,
  onChanged,
}: {
  row: ConditionReportExceptionRow;
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState<null | 'retry' | 'resolve'>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [reason, setReason] = useState('');

  const runRetry = async () => {
    setBusy('retry');
    setError(null);
    try {
      await retryConditionReport(row.zInspectorDocumentId);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry');
    } finally {
      setBusy(null);
    }
  };

  const runResolve = async () => {
    if (reason.trim().length === 0) {
      setError('A reason is required to resolve.');
      return;
    }
    setBusy('resolve');
    setError(null);
    try {
      await resolveConditionReport(row.zInspectorDocumentId, reason.trim());
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div className="cluster" style={{ justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 'var(--weight-medium)' }}>{row.propertyLabel || '(unresolved unit)'}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)' }}>
            {[row.residentNames, `seen ${fmtTime(row.firstSeenAt)}`].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      {(row.exceptionReason || row.sendError) && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
          {row.exceptionReason && <div>{row.exceptionReason}</div>}
          {row.sendError && (
            <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
              {row.sendError}
            </div>
          )}
        </div>
      )}

      {error && (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div>
      )}

      {resolving ? (
        <div className="stack-sm" style={{ gap: 'var(--space-2)' }}>
          <label className="field">
            <span className="label">Resolution reason</span>
            <input
              type="text"
              className="input"
              value={reason}
              placeholder="Why is this being dismissed?"
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
          </label>
          <div className="cluster" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn"
              data-variant="primary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => void runResolve()}
            >
              {busy === 'resolve' ? 'Resolving…' : 'Confirm resolve'}
            </button>
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => { setResolving(false); setReason(''); setError(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="cluster" style={{ gap: 'var(--space-2)' }}>
          {row.retryable && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => void runRetry()}
            >
              {busy === 'retry' ? 'Re-arming…' : 'Retry'}
            </button>
          )}
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            disabled={busy !== null}
            onClick={() => { setResolving(true); setError(null); }}
          >
            Resolve
          </button>
        </div>
      )}
    </div>
  );
}
